import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import SeoJsonLd from "@/components/SeoJsonLd";
import { useTitle } from "@/hooks/use-title";
import { CASE_STUDIES } from "@/data/content";

function ChipList({ label, items }) {
  return (
    <div className="mt-8">
      {label && (
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">{label}</p>
      )}
      <ul className="mt-4 flex flex-wrap gap-2">
        {items.map((c) => (
          <li
            key={c}
            className="border border-line/60 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-300 transition-colors duration-300 hover:border-glow/50 hover:text-glow"
          >
            {c}
          </li>
        ))}
      </ul>
    </div>
  );
}

function BulletList({ label, items }) {
  return (
    <div className="mt-8">
      {label && (
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">{label}</p>
      )}
      <ul className="mt-2 divide-y divide-line/50 border-y border-line/50">
        {items.map((b) => (
          <li key={b} className="flex items-baseline gap-4 py-3">
            <span className="inline-block h-1.5 w-1.5 shrink-0 rotate-45 border border-glow/60" aria-hidden="true" />
            <span className="text-sm leading-relaxed text-slate-300">{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Chain({ label, items }) {
  return (
    <div className="mt-8">
      {label && (
        <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">{label}</p>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-y-4 border border-line/60 bg-ink p-6">
        {items.map((node, i) => (
          <span key={i} className="flex items-center">
            <span
              className={`border px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] ${
                i === items.length - 1
                  ? "border-glow/70 bg-glow/5 text-glow"
                  : "border-line/70 text-slate-300"
              }`}
            >
              {node}
            </span>
            {i < items.length - 1 && (
              <ArrowRight size={13} className="mx-2 shrink-0 text-slate-600" aria-hidden="true" />
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

function Steps({ items }) {
  return (
    <ol className="mt-10 border-b border-line/50">
      {items.map((st, i) => (
        <li key={st.n} className="grid gap-3 border-t border-line/50 py-7 md:grid-cols-12" data-testid={`work-step-${st.n}`}>
          <span className="font-mono text-xs tracking-[0.3em] text-glow/70 md:col-span-1">{st.n}</span>
          <div className="md:col-span-11">
            <h3 className="text-xl font-medium tracking-tight text-slate-100">{st.title}</h3>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-400">{st.text}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

export default function WorkCase() {
  const { slug } = useParams();
  const item = CASE_STUDIES.find((c) => c.slug === slug);
  useTitle(item ? `${item.title} — Selected Work — Operating Reality` : "Selected Work — Operating Reality");

  if (!item) {
    return (
      <main className="wrap flex min-h-screen flex-col items-start justify-center pt-24" data-testid="work-case-not-found">
        <p className="kicker">Case study not found</p>
        <h1 className="mt-6 text-4xl font-semibold tracking-tight text-slate-100">
          This study hasn't been published yet.
        </h1>
        <Link to="/work" className="btn-primary mt-10" data-testid="work-case-back">
          Back to Selected Work
        </Link>
      </main>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `${item.title}: ${item.subtitle}`,
    description: item.sections[0]?.paragraphs?.[0] || item.subtitle,
    inLanguage: "en-GB",
    mainEntityOfPage: `https://operatingreality.co.uk/work/${item.slug}`,
    author: {
      "@type": "Person",
      name: "Anna Krat",
      url: "https://annakrat.co.uk/",
    },
    publisher: {
      "@type": "Organization",
      name: "Operating Reality",
      url: "https://operatingreality.co.uk",
      logo: {
        "@type": "ImageObject",
        url: "https://operatingreality.co.uk/assets/or-mark.png",
      },
    },
  };

  return (
    <main className="pt-16 lg:pt-[72px]" data-testid={`work-case-${item.slug}`}>
      <SeoJsonLd id={`ld-${item.slug}`} data={articleSchema} />
      <section className="relative overflow-hidden border-b border-line/50">
        <div className="bg-grid absolute inset-0 opacity-25" />
        <div className="wrap relative py-20 lg:py-28">
          <Reveal>
            <Link
              to="/work"
              className="group inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 transition-colors hover:text-glow"
              data-testid="work-case-back-link"
            >
              <ArrowLeft size={13} className="transition-transform group-hover:-translate-x-1" />
              Selected Work
            </Link>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <span className="border border-glow/50 bg-glow/5 px-3.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.28em] text-glow" data-testid="work-case-label">
                Case study
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">
                {item.tag}
              </span>
            </div>
            <h1 className="mt-8 max-w-4xl text-balance text-4xl font-semibold leading-[1.08] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
              {item.title}
            </h1>
            <p className="mt-5 max-w-2xl text-balance text-xl font-medium leading-snug tracking-tight text-slate-300 sm:text-2xl">
              {item.subtitle}
            </p>
          </Reveal>
        </div>
      </section>

      {item.sections.map((s, idx) => (
        <section
          key={s.kicker}
          className={`border-b border-line/50 ${idx % 2 === 1 ? "bg-charcoal/20" : ""}`}
          data-testid={`work-section-${s.kicker.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
          <div className="wrap py-16 lg:py-20">
            <div className="max-w-3xl">
              <Reveal>
                <p className="kicker">{s.kicker}</p>
                {s.quote ? (
                  <div className="mt-8">
                    <p className="text-balance text-3xl font-medium leading-[1.15] tracking-tight text-slate-50 sm:text-4xl">
                      {s.quote}
                    </p>
                    <p className="mt-6 max-w-2xl text-lg leading-relaxed text-glow/90">
                      {s.quoteAccent}
                    </p>
                  </div>
                ) : (
                  <>
                    {(s.paragraphs || []).map((p, i) => (
                      <p key={i} className="mt-6 text-base leading-relaxed text-slate-300 sm:text-lg">
                        {p}
                      </p>
                    ))}
                    {s.standout && (
                      <p className="mt-8 border-l-2 border-glow/60 pl-6 text-xl font-medium leading-snug tracking-tight text-slate-100 sm:text-2xl">
                        {s.standout}
                      </p>
                    )}
                    {s.chips && <ChipList label={s.chipsLabel} items={s.chips} />}
                    {s.bullets && <BulletList label={s.bulletsLabel} items={s.bullets} />}
                    {s.chain && <Chain label={s.chainLabel} items={s.chain} />}
                    {s.steps && <Steps items={s.steps} />}
                    {(s.afterParagraphs || []).map((p, i) => (
                      <p key={i} className="mt-8 text-base leading-relaxed text-slate-400">
                        {p}
                      </p>
                    ))}
                  </>
                )}
              </Reveal>
            </div>
          </div>
        </section>
      ))}

      <section className="bg-charcoal/20">
        <div className="wrap py-20 lg:py-28">
          <Reveal className="max-w-3xl">
            <p className="kicker">Facing something similar?</p>
            <h2 className="mt-6 text-balance text-3xl font-semibold leading-[1.1] tracking-tight text-slate-50 sm:text-4xl">
              Have a problem you can't quite explain?
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
              If your organisation is mid-migration, mid-transformation or mid-governance —
              that's usually the right moment to look underneath it.
            </p>
            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
              <Link to="/contact" className="btn-primary" data-testid="work-case-cta-conversation">
                Start a conversation <ArrowRight size={14} />
              </Link>
              <Link to="/consulting" className="btn-ghost" data-testid="work-case-cta-consulting">
                Explore Consulting
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
